/* eslint-disable import/prefer-default-export */
import { applyMiddleware, compose, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { all, spawn } from 'redux-saga/effects';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';

import _flattenDeep from 'lodash/flattenDeep';

import { getLogger } from '../logger';

import { createReducer } from './reducer';
import { createUploadReducer } from './upload-descriptor';

const logger = getLogger('redux setup');
const errorLogger = getLogger('saga uncaught error');

global.isProduction = process.env.NODE_ENV !== 'development';
global.debug = !global.isProduction;

const composeEnhancers = isProduction
  ? compose
  : composeWithDevTools({
      // INFO: Specify name here, actionsBlacklist, actionsCreators and other options if needed
    });

function createMiddlewares(additionalMiddlewares, sagaContext) {
  const middlewares = [...additionalMiddlewares];
  const sagaMiddleware = createSagaMiddleware({
    context: sagaContext,
    onError: (e, config) => errorLogger.error(e, config),
  });
  middlewares.push(thunk);

  return [middlewares, sagaMiddleware];
}

function createWatcher(sagas) {
  return function* watchAppActions() {
    yield all(sagas.map(watcher => spawn(watcher)));
  };
}

export function setupRedux(middlewares, globalReducers = {}, globalSagas = [], initialState, config = {}) {
  if (setupRedux.isInitialized) {
    return setupRedux.store;
  }
  setupRedux.isInitialized = true;
  logger.debug(
    'Setup redux with: Middlwares',
    middlewares,
    'Reducers',
    globalReducers,
    'Sagas',
    globalSagas,
    'InitialState',
    { ...initialState, messages: {} },
    'Configuration',
    config
  );
  const modules = Object.values(global.reduxModules);

  // INFO: Collecting all configurations from modules and override with global settings
  const configuration = modules.reduce((state, moduleDescriptor) => {
    return { ...moduleDescriptor.__config, ...state };
  }, config);

  logger.debug('Combined configuration', configuration);

  const reducerCollection = modules.reduce((reducers, moduleDescriptor) => {
    // INFO: Initialize each module with the configuration
    moduleDescriptor.__initialize(configuration);
    // eslint-disable-next-line no-param-reassign
    return { ...reducers, [moduleDescriptor.name]: moduleDescriptor.__reducer };
  }, globalReducers);

  // We use the same reducer name in the web app for different functionality, changed to x_upload
  reducerCollection.x_upload = createUploadReducer();

  logger.debug('Combined reducers', reducerCollection);
  const sagaCollection = modules.reduce((sagas, moduleDescriptor) => [...sagas, moduleDescriptor.__sagas], globalSagas);
  logger.debug('Combined sagas', sagaCollection);
  // INFO: Also use the configuration as the saga context
  const [combinedMiddlewares, sagaMiddleware] = createMiddlewares(middlewares, {
    ...configuration,
  });
  const rootReducer = createReducer(reducerCollection);
  const store = createStore(
    rootReducer,
    initialState || {},
    composeEnhancers(applyMiddleware(...combinedMiddlewares, sagaMiddleware))
  );
  setupRedux.store = store;

  const watchers = _flattenDeep(sagaCollection);
  if (watchers.length > 0) {
    sagaMiddleware.run(createWatcher(watchers));
  }
  store.dispatch({ type: 'APP_INIT' });
  logger.info('App initialized');
  global.isReduxSetup = true;
  logger.info('Redux is setup');
  return store;
}
